/**
 * Admin "Approvals" queue — Service completion submissions awaiting admin action.
 * Approve fires invoice + warranty + customer email. Reject sends back. Revisit creates follow-up booking.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  CheckCircle2, XCircle, RefreshCw, Loader2, Image as ImageIcon, MapPin, Clock,
  AlertCircle, ChevronRight, Phone, Mail, FileText, Bug, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const fmtDateTime = (s) => (s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export default function ApprovalsAdminTab({ api, onJump }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [err, setErr] = useState('');
  const [showForce, setShowForce] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await api('GET', '/admin/approvals?status=awaiting_approval');
      setRows(r.data?.items || []);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="approvals-admin-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2"><CheckCircle2 size={20} className="text-emerald-700" /> Service Approvals</h1>
          <p className="text-sm text-slate-500">Review completed jobs · Approve to fire invoice + warranty + customer notification automatically.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowForce(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="approvals-force-complete-btn">
            <AlertCircle size={14} className="mr-1.5" /> Force Complete (Tech Offline)
          </Button>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 mb-3">{err}</div>}

      {loading && <div className="text-center text-slate-400 py-10"><Loader2 className="inline animate-spin" size={20} /></div>}
      {!loading && rows.length === 0 && <div className="text-center text-slate-400 py-12 bg-white border border-slate-200 rounded-xl">No completions awaiting your approval. Good work team! 🎉</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(r => (
          <div key={r.booking_id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition" data-testid={`approval-card-${r.booking_id}`}>
            <div className="flex items-start justify-between mb-2 gap-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">{r.customer_name}</div>
                <div className="text-[11px] text-slate-500">{r.customer_phone}</div>
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                <Clock size={9} /> AWAITING
              </span>
            </div>
            <div className="space-y-1.5 text-xs mb-3">
              <div className="flex items-center gap-2 text-slate-600"><Bug size={12} className="text-emerald-700" /> {r.completion?.target_pest || r.service_label || '—'}</div>
              <div className="flex items-center gap-2 text-slate-600"><MapPin size={12} className="text-emerald-700" /> {r.completion?.gps_lat?.toFixed(4)}, {r.completion?.gps_lng?.toFixed(4)}</div>
              <div className="flex items-center gap-2 text-slate-600"><ImageIcon size={12} className="text-emerald-700" /> {(r.completion?.before_photos?.length || 0)} before · {(r.completion?.after_photos?.length || 0)} after</div>
              <div className="flex items-center gap-2 text-slate-500"><Clock size={12} /> Submitted {fmtDateTime(r.submitted_at)}</div>
            </div>
            <Button onClick={() => setActive(r)} className="w-full bg-emerald-700 hover:bg-emerald-800" size="sm" data-testid={`approval-open-${r.booking_id}`}>
              Review <ChevronRight size={13} className="ml-1" />
            </Button>
          </div>
        ))}
      </div>

      {active && <ReviewModal api={api} item={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
      {showForce && <ForceCompleteModal api={api} onClose={() => setShowForce(false)} onDone={() => { setShowForce(false); load(); }} onOpenInvoice={(bookingId) => {
        setShowForce(false);
        // Stash the prefill request, then jump to Invoices tab — InvoicesAdminTab will pick it up.
        window.__entoridPendingInvoicePrefill = bookingId;
        window.dispatchEvent(new CustomEvent('entorid:open-invoice-creator', { detail: { bookingId } }));
        onJump?.('invoices');
      }} />}
    </div>
  );
}

function ReviewModal({ api, item, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [invoiceType, setInvoiceType] = useState('gst'); // 'gst' | 'non_gst'
  const [issueWarranty, setIssueWarranty] = useState(true);
  const c = item.completion || {};

  const act = async (action, extra = {}) => {
    if ((action === 'reject' || action === 'revisit') && !reason.trim()) {
      setErr(`Please provide a reason to ${action === 'reject' ? 'reject' : 'schedule a revisit'}`); return;
    }
    setBusy(true); setErr('');
    try {
      const r = await api('POST', `/admin/approvals/${item.booking_id}/${action}`, { reason: reason || undefined, ...extra });
      setResult({ action, ...r.data });
      setApproveOpen(false);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="approval-result">
        <div className="bg-white max-w-md w-full rounded-2xl p-6 text-center">
          <CheckCircle2 size={48} className="text-emerald-600 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            {result.action === 'approve' && 'Approved!'}
            {result.action === 'reject' && 'Rejected'}
            {result.action === 'revisit' && 'Revisit Scheduled'}
          </h3>
          {result.action === 'approve' && (
            <div className="space-y-1 text-sm text-slate-600 mb-4">
              {result.invoice?.invoice_number && <div>✅ Invoice <b>{result.invoice.invoice_number}</b> created</div>}
              {result.warranty?.warranty_number && <div>🛡️ Warranty <b>{result.warranty.warranty_number}</b> issued</div>}
              <div>📧 Customer notified by email</div>
            </div>
          )}
          {result.action === 'revisit' && <div className="text-sm text-slate-600 mb-4">New booking <b>{result.booking_code}</b> created.</div>}
          <Button onClick={onDone} className="bg-emerald-700 hover:bg-emerald-800 w-full" data-testid="approval-result-close">Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="approval-modal">
      <div className="bg-white w-full sm:max-w-3xl rounded-2xl overflow-hidden max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-emerald-700 text-white">
          <div><div className="text-xs opacity-90">Service Completion Review</div><div className="font-bold">{item.customer_name}</div></div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Customer</div>
              <div className="text-sm font-semibold text-slate-900">{item.customer_name}</div>
              <div className="text-xs text-slate-600 flex items-center gap-1"><Phone size={10} /> {item.customer_phone}</div>
              {item.customer_email && <div className="text-xs text-slate-600 flex items-center gap-1"><Mail size={10} /> {item.customer_email}</div>}
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Service</div>
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-1"><Bug size={12} className="text-emerald-700" /> {c.target_pest || item.service_label}</div>
              <div className="text-xs text-slate-600 mt-1">Submitted by <b>{c.submitted_by_name || '—'}</b></div>
              <div className="text-xs text-slate-500">{fmtDateTime(item.submitted_at)}</div>
            </div>
          </div>

          {/* Before/After Photos */}
          {(c.before_photos?.length || c.after_photos?.length) ? (
            <div className="grid grid-cols-2 gap-3">
              <PhotoGroup label="BEFORE" photos={c.before_photos || []} />
              <PhotoGroup label="AFTER" photos={c.after_photos || []} />
            </div>
          ) : null}

          {/* Completion details */}
          <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-2 gap-3 text-xs">
            <Detail label="Chemicals Used" value={c.chemicals_used} />
            <Detail label="Quantity" value={c.quantity_used} />
            <Detail label="Service Duration" value={c.service_duration_min ? `${c.service_duration_min} min` : null} />
            <Detail label="Property Type" value={c.property_type} />
            <Detail label="GPS" value={c.gps_lat ? `${c.gps_lat.toFixed(5)}, ${c.gps_lng?.toFixed(5)}` : null} />
            <Detail label="Customer Feedback" value={c.customer_feedback} />
          </div>

          {c.service_notes && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1">Technician Notes</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.service_notes}</div>
            </div>
          )}

          {c.customer_signature && (
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Customer Signature</div>
              <img src={c.customer_signature} alt="signature" className="max-h-32 border border-slate-100 rounded" />
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-slate-700">Notes (required for Reject / Revisit)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="e.g., AFTER photo missing the corner; please redo." data-testid="approval-reason" />
          </div>

          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 grid grid-cols-3 gap-2">
          <Button variant="outline" onClick={() => act('reject')} disabled={busy} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="approval-reject">
            <XCircle size={14} className="mr-1" /> Reject
          </Button>
          <Button variant="outline" onClick={() => act('revisit')} disabled={busy} className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="approval-revisit">
            <RefreshCw size={14} className="mr-1" /> Revisit
          </Button>
          <Button onClick={() => { setErr(''); setApproveOpen(true); }} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="approval-approve">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle2 size={14} className="mr-1" /> Approve</>}
          </Button>
        </div>
      </div>

      {approveOpen && (
        <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setApproveOpen(false); }} data-testid="approve-options-modal">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-emerald-700 text-white px-5 py-3.5 flex items-center justify-between">
              <div className="font-bold flex items-center gap-2"><CheckCircle2 size={18} /> Approve &amp; Generate</div>
              <button onClick={() => setApproveOpen(false)} className="text-white/80 hover:text-white" data-testid="approve-options-close"><XCircle size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">Choose invoice type</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setInvoiceType('gst')} data-testid="approve-invoice-gst"
                    className={`rounded-xl border-2 p-3 text-left transition-all ${invoiceType === 'gst' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="font-bold text-slate-900 flex items-center gap-1.5">{invoiceType === 'gst' && <CheckCircle2 size={14} className="text-emerald-600" />} GST Invoice</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">18% GST, GSTIN-compliant tax invoice</div>
                  </button>
                  <button type="button" onClick={() => setInvoiceType('non_gst')} data-testid="approve-invoice-nongst"
                    className={`rounded-xl border-2 p-3 text-left transition-all ${invoiceType === 'non_gst' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="font-bold text-slate-900 flex items-center gap-1.5">{invoiceType === 'non_gst' && <CheckCircle2 size={14} className="text-emerald-600" />} Non-GST Invoice</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">No tax, simple bill of supply</div>
                  </button>
                </div>
              </div>

              <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:bg-slate-50" data-testid="approve-warranty-label">
                <input type="checkbox" checked={issueWarranty} onChange={(e) => setIssueWarranty(e.target.checked)} className="w-4 h-4 mt-0.5" data-testid="approve-warranty-checkbox" />
                <div>
                  <div className="font-semibold text-slate-800 text-sm">Issue warranty certificate</div>
                  <div className="text-[11px] text-slate-500">{issueWarranty ? 'A warranty card will be generated for this service.' : 'No warranty card will be generated.'}</div>
                </div>
              </label>

              <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                On approve: a <b>{invoiceType === 'gst' ? 'GST' : 'Non-GST'}</b> invoice is generated{issueWarranty ? ', a warranty certificate is issued' : ' (no warranty)'}, and the customer is emailed.
              </div>
              {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
            </div>
            <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={busy} data-testid="approve-options-cancel">Cancel</Button>
              <Button onClick={() => act('approve', { is_gst: invoiceType === 'gst', issue_warranty: issueWarranty })} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="approve-options-confirm">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle2 size={14} className="mr-1" /> Confirm Approve</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PhotoGroup({ label, photos }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">{label} ({photos.length})</div>
      <div className="grid grid-cols-3 gap-1.5">
        {photos.length === 0 && <div className="col-span-3 text-xs text-slate-400 text-center py-4">No photos</div>}
        {photos.slice(0, 6).map((p, i) => (
          <a key={i} href={p} target="_blank" rel="noreferrer" className="block aspect-square bg-slate-100 rounded overflow-hidden">
            <img src={p} alt={`${label}-${i}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = '0.3'; }} />
          </a>
        ))}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-800">{value || '—'}</div>
    </div>
  );
}


// ============================================================
// ForceCompleteModal — bypass technician submission
// Admin picks an in-progress/scheduled booking, supplies the minimum service
// details (pest, chemical, qty, duration, reason), and the backend auto-generates
// the invoice + warranty and emails the customer. Used when technician's mobile
// is dead or they cannot submit the completion form.
// ============================================================
// Normalize FastAPI / axios error payloads — sometimes detail is a string,
// sometimes it's an array of Pydantic ValidationError objects. Rendering an
// object directly in JSX crashes React ("Objects are not valid as a React child").
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join('; ');
  if (typeof d === 'object') return d.msg || JSON.stringify(d);
  return String(d);
};

// Common pest → chemical mapping (auto-suggest based on booking pest_type).
// Admin can still pick "Other" and type any custom chemical.
const CHEMICAL_OPTIONS = {
  termite: ['Termidor SC 9.1% (Fipronil)', 'Premise 200 SC (Imidacloprid)', 'Chloropyrifos 20% EC'],
  cockroach: ['Maxforce Gel Bait', 'Advion Cockroach Gel', 'Deltamethrin 2.5% SC'],
  'bed bug': ['Lambda Cyhalothrin 5% EC', 'Aqua K-Othrine', 'Deltamethrin 2.5% SC'],
  rodent: ['Bromadiolone 0.005%', 'Brodifacoum 0.005%', 'Zinc Phosphide 80%'],
  mosquito: ['Cyfluthrin 5% EW', 'Pyrethrin 0.5%', 'Lambda Cyhalothrin 5% EC'],
  ant: ['Maxforce Quantum Ant Bait', 'Fipronil 2.92% Gel', 'Deltamethrin 2.5% SC'],
  general: ['Deltamethrin 2.5% SC', 'Cypermethrin 10% EC', 'Lambda Cyhalothrin 5% EC'],
  'wood borer': ['Boric Acid Solution', 'Chloropyrifos 20% EC', 'Permethrin 25% EC'],
};
const ALL_CHEMICALS = [
  ...new Set(Object.values(CHEMICAL_OPTIONS).flat()),
];

function ForceCompleteModal({ api, onClose, onDone, onOpenInvoice }) {
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bookingQ, setBookingQ] = useState('');
  const [datePreset, setDatePreset] = useState('30d');
  const [form, setForm] = useState({
    target_pest: '', chemicals_used: '', quantity_used: '',
    service_duration_min: '', property_type: '', service_notes: '',
    admin_reason: 'Technician offline — manual completion by admin',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api('GET', '/admin/bookings');
        const open = (r.data || []).filter((b) => !['completed', 'warranty_active', 'cancelled'].includes(b.status));
        setBookings(open);
      } catch { /* */ }
    })();
  }, [api]);

  // Filter bookings by search query + date preset
  const filteredBookings = (() => {
    const today = new Date();
    const minusDays = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
    const todayISO = today.toISOString().slice(0, 10);
    let from = '', to = todayISO;
    if (datePreset === 'today') from = todayISO;
    else if (datePreset === '7d') from = minusDays(7);
    else if (datePreset === '30d') from = minusDays(30);
    else if (datePreset === 'all') { from = ''; to = ''; }
    const q = bookingQ.trim().toLowerCase();
    return bookings.filter((b) => {
      if (from && (b.scheduled_date || '') < from) return false;
      if (to && (b.scheduled_date || '') > to + 'T23:59:59') return false;
      if (!q) return true;
      return (b.booking_code || '').toLowerCase().includes(q)
        || (b.customer_name || '').toLowerCase().includes(q)
        || (b.customer_phone || '').toLowerCase().includes(q)
        || (b.customer_email || '').toLowerCase().includes(q);
    });
  })();

  const submit = async () => {
    setErr('');
    if (!selected) return setErr('Select a booking');
    if (!form.target_pest.trim()) return setErr('Target pest is required');
    if (!form.chemicals_used.trim()) return setErr('Chemicals used is required');
    if (!window.confirm('Force-complete this booking?\n\nThis will:\n• Mark the booking as completed\n• Generate the TIPC invoice\n• Issue the warranty certificate\n• Email the customer.\n\nUse only when technician cannot submit.')) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        service_duration_min: form.service_duration_min ? Number(form.service_duration_min) : null,
      };
      const r = await api('POST', `/admin/approvals/${selected.id}/force-complete`, payload);
      setResult(r.data);
    } catch (e) { setErr(fmtErr(e)); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3"><CheckCircle2 size={28} /></div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Force-Complete Successful</h3>
          <div className="space-y-1 text-sm text-slate-600 mb-4 text-left">
            {result.invoice?.invoice_number && <div>✅ Invoice <b>{result.invoice.invoice_number}</b> created</div>}
            {result.warranty?.warranty_number && <div>🛡️ Warranty <b>{result.warranty.warranty_number}</b> issued</div>}
            <div>📧 Customer notified by email</div>
          </div>
          <div className="space-y-2">
            <Button
              onClick={() => { onOpenInvoice?.(selected?.id); }}
              className="bg-indigo-600 hover:bg-indigo-700 w-full"
              data-testid="force-result-open-invoice"
            >
              ✨ Open Invoice Creator (review / edit before sending)
            </Button>
            <Button
              onClick={async () => {
                try {
                  if (result.invoice?.id) {
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    window.open(`${API_URL}/api/panel/admin/invoices/${result.invoice.id}/pdf`, '_blank');
                  }
                } catch { /* ignore */ }
                onDone();
              }}
              className="bg-emerald-600 hover:bg-emerald-700 w-full"
              data-testid="force-result-direct-pdf"
            >
              📄 Done — Use Auto-Generated Invoice (download PDF)
            </Button>
            <Button onClick={onDone} variant="outline" className="w-full" data-testid="force-result-close">Close</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="force-modal">
      <div className="bg-white w-full sm:max-w-xl rounded-2xl overflow-hidden max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-amber-600 text-white">
          <div>
            <div className="text-xs opacity-90 flex items-center gap-1"><AlertCircle size={12} /> Force Complete</div>
            <div className="font-bold">Bypass Technician Submission</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-3 text-sm">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            ⚠️ Use only when the technician&apos;s phone is down or they cannot submit the completion form. This bypasses photos, GPS and customer signature.
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Select Booking *</label>
            {selected ? (
              <div className="mt-1 bg-emerald-50 border border-emerald-300 rounded-md px-3 py-2 flex items-center justify-between gap-2" data-testid="force-booking-picked">
                <div className="text-sm min-w-0">
                  <div className="font-semibold text-emerald-900">✅ {selected.booking_code} · {selected.customer_name}</div>
                  <div className="text-[11px] text-emerald-700 truncate">
                    {selected.scheduled_date} · {selected.status} · 📞 {selected.customer_phone}
                    {selected.customer_email && <> · ✉ {selected.customer_email}</>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelected(null); setForm((f) => ({ ...f, target_pest: '', chemicals_used: '', property_type: '' })); }}
                  className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold underline shrink-0"
                  data-testid="force-booking-change"
                >Change</button>
              </div>
            ) : (
              <div className="mt-1 space-y-2">
                <input
                  value={bookingQ}
                  onChange={(e) => setBookingQ(e.target.value)}
                  placeholder="🔎 Search by name, booking ID, mobile, or email…"
                  className="w-full h-10 px-3 rounded-md border border-slate-300"
                  data-testid="force-booking-search"
                />
                <div className="flex flex-wrap gap-1.5" data-testid="force-booking-date-filter">
                  {[['today', 'Today'], ['7d', '7 Days'], ['30d', '30 Days'], ['all', 'All']].map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setDatePreset(k)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${datePreset === k ? 'bg-amber-600 text-white' : 'bg-white border border-slate-300 text-slate-700'}`}
                      data-testid={`force-date-${k}`}
                    >{l}</button>
                  ))}
                </div>
                <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white">
                  {filteredBookings.length === 0 ? (
                    <div className="text-center text-xs text-slate-400 py-6">No matching bookings.</div>
                  ) : (
                    filteredBookings.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => {
                          setSelected(b);
                          // Auto-suggest chemical for the booking's pest type (only Chemicals Used — admin still confirms)
                          const pestKey = (b.pest_type || b.service_label || '').toLowerCase();
                          const matchKey = Object.keys(CHEMICAL_OPTIONS).find((k) => pestKey.includes(k));
                          const suggested = matchKey ? CHEMICAL_OPTIONS[matchKey][0] : '';
                          setForm((f) => ({
                            ...f,
                            target_pest: f.target_pest || b.service_label || b.pest_type || '',
                            chemicals_used: suggested,
                            property_type: f.property_type || b.property_size || '',
                          }));
                        }}
                        className="w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 hover:bg-amber-50"
                        data-testid={`force-booking-row-${b.id}`}
                      >
                        <div className="font-semibold">{b.booking_code} · {b.customer_name}</div>
                        <div className="text-[11px] text-slate-500">
                          {b.scheduled_date} · {b.status} · 📞 {b.customer_phone}
                          {b.customer_email && <> · ✉ {b.customer_email}</>}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Target Pest *</label>
              <input value={form.target_pest} onChange={(e) => setForm((f) => ({ ...f, target_pest: e.target.value }))} className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300" placeholder="e.g. Termite" data-testid="force-pest" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Chemicals Used *</label>
              {(() => {
                const pestKey = (selected?.pest_type || selected?.service_label || '').toLowerCase();
                const matchKey = Object.keys(CHEMICAL_OPTIONS).find((k) => pestKey.includes(k));
                const pestSpecific = matchKey ? CHEMICAL_OPTIONS[matchKey] : [];
                const otherChems = ALL_CHEMICALS.filter((c) => !pestSpecific.includes(c));
                const isOther = form.chemicals_used === 'OTHER';
                const isCustom = form.chemicals_used && form.chemicals_used !== 'OTHER' && ![...pestSpecific, ...otherChems].includes(form.chemicals_used);
                const selectVal = isCustom ? 'OTHER' : (form.chemicals_used || '');
                return (
                  <>
                    <select
                      value={selectVal}
                      onChange={(e) => {
                        const v = e.target.value;
                        // Picking "Other" — clear value so the textarea below takes over
                        setForm((f) => ({ ...f, chemicals_used: v === 'OTHER' ? '' : v }));
                      }}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
                      data-testid="force-chem-select"
                    >
                      <option value="">— Pick a chemical —</option>
                      {pestSpecific.length > 0 && (
                        <optgroup label={`Recommended for ${matchKey}`}>
                          {pestSpecific.map((c) => <option key={c} value={c}>{c}</option>)}
                        </optgroup>
                      )}
                      {otherChems.length > 0 && (
                        <optgroup label="All Other Chemicals">
                          {otherChems.map((c) => <option key={c} value={c}>{c}</option>)}
                        </optgroup>
                      )}
                      <option value="OTHER">Other (type below) …</option>
                    </select>
                    {(selectVal === 'OTHER' || isCustom) && (
                      <textarea
                        value={form.chemicals_used || ''}
                        onChange={(e) => setForm((f) => ({ ...f, chemicals_used: e.target.value }))}
                        rows={2}
                        placeholder="Type the chemical name(s) used — e.g. Custom blend: Imidacloprid 30.5% SC + Bifenthrin 8% EC"
                        className="mt-2 w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
                        data-testid="force-chem-other"
                      />
                    )}
                  </>
                );
              })()}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Quantity Used</label>
              <input value={form.quantity_used} onChange={(e) => setForm((f) => ({ ...f, quantity_used: e.target.value }))} className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300" placeholder="e.g. 500 ml" data-testid="force-qty" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Duration (min)</label>
              <input type="number" value={form.service_duration_min} onChange={(e) => setForm((f) => ({ ...f, service_duration_min: e.target.value }))} className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300" placeholder="60" data-testid="force-dur" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-600">Property Type</label>
              <input value={form.property_type} onChange={(e) => setForm((f) => ({ ...f, property_type: e.target.value }))} className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300" placeholder="e.g. 2 BHK Apartment" data-testid="force-prop" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Admin Reason *</label>
            <input value={form.admin_reason} onChange={(e) => setForm((f) => ({ ...f, admin_reason: e.target.value }))} className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300" data-testid="force-reason" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Service Notes</label>
            <textarea value={form.service_notes} onChange={(e) => setForm((f) => ({ ...f, service_notes: e.target.value }))} rows={2} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" placeholder="Anything special the customer should know" data-testid="force-notes" />
          </div>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-2" data-testid="force-error"><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="force-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Generating…</> : 'Force-Complete & Generate Invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
}
